import {
  CustomFormElement,
  FORM_BORDER_COLORS,
  FORM_FILL_COLORS,
  FORM_TOKENS,
  FormBorderColor,
  FormCondition,
  FormFieldBorder,
  FormFillColor,
  FormTokenSource,
  HIDE_IF_TOKEN_MISSING_SOURCES,
} from '../types/CustomForm.type';

/**
 * Runtime helpers for Custom Forms. These were split out of CustomForm.type.ts
 * so load-bearing logic (token parsing, conditional visibility, CSS builders,
 * required-field checks) isn't hidden behind a "types" file. Pure functions —
 * shared by the builder, the fill UIs, and the server-side PDF renderer so
 * behavior can't drift between them.
 */

// ── Form name ────────────────────────────────────────────────────────────────
// A form carries both an internal `name` and an optional patient-facing
// `displayName`. Surfaces historically diverged (generate used `name`, Request
// Info used `displayName`); this is the single source of truth so every access
// point shows the same label: the display name when set, else the name.
export const customFormDisplayName = (form: {
  name: string;
  displayName?: string | null;
}): string => (form.displayName && form.displayName.trim()) || form.name;

// ── Inline tokens in static text ─────────────────────────────────────────────
// A static-text block may embed tokens mid-sentence as `{{tokenId}}`, e.g.
// "I, {{patient.legalFirstName}} {{patient.legalLastName}}, authorize ...".
// Only known token ids are treated as tokens; anything else is left verbatim.
const INLINE_TOKEN_PATTERN = /\{\{\s*([\w.]+)\s*\}\}/g;
const VALID_TOKEN_ID_SET = new Set<string>(FORM_TOKENS.map(t => t.id));

/** Known token ids referenced inline in a static-text string. */
export const extractInlineTokenIds = (text: string): string[] => {
  const ids: string[] = [];
  for (const m of (text || '').matchAll(INLINE_TOKEN_PATTERN)) {
    if (VALID_TOKEN_ID_SET.has(m[1])) ids.push(m[1]);
  }
  return ids;
};

/** Inline `{{...}}` references that are NOT known token ids (typos) — these would
 * print verbatim on the rendered document, so the builder can warn about them. */
export const extractUnknownInlineTokenIds = (text: string): string[] => {
  const ids: string[] = [];
  for (const m of (text || '').matchAll(INLINE_TOKEN_PATTERN)) {
    if (!VALID_TOKEN_ID_SET.has(m[1])) ids.push(m[1]);
  }
  return ids;
};

/** True if the text embeds at least one known inline token. */
export const hasInlineTokens = (text: string): boolean => extractInlineTokenIds(text).length > 0;

/**
 * The free-text content of an element that may embed inline `{{tokens}}`: a static
 * text block's text, or the default value of a single/multi-line/number field
 * (so those fields can be pre-filled with token values, resolved at generation).
 */
export const elementInlineTokenText = (el: CustomFormElement): string => {
  if (el.type === 'staticText') return el.text || '';
  if (el.type === 'singleLine' || el.type === 'multiLine' || el.type === 'number') {
    return el.value || '';
  }
  return '';
};

/** Replace `{{tokenId}}` with resolved values; unknown ids are left verbatim and
 * known-but-unresolved ids resolve to an empty string. */
export const resolveInlineTokens = (text: string, values: Record<string, string>): string =>
  (text || '').replace(INLINE_TOKEN_PATTERN, (full, id) =>
    VALID_TOKEN_ID_SET.has(id) ? (values[id] ?? '') : full
  );

/** For the builder: render `{{tokenId}}` as a friendly `{Label}` so authors see
 * which token sits where (design-time has no resolved values). */
export const inlineTokenLabels = (text: string): string =>
  (text || '').replace(INLINE_TOKEN_PATTERN, (full, id) => {
    const tok = FORM_TOKENS.find(t => t.id === id);
    return tok ? `{${tok.label}}` : full;
  });

/** A run of literal text, or a single known inline token (its friendly label). */
export type InlineTextSegment = { type: 'text'; value: string } | { type: 'token'; value: string };

/**
 * Split static text into literal runs and known-token segments so the builder's
 * edit view can render the tokens as distinct chips. Unknown `{{ids}}` are left
 * inside the surrounding literal run (they print verbatim on the document).
 */
export const splitInlineTokens = (text: string): InlineTextSegment[] => {
  const src = text || '';
  const segments: InlineTextSegment[] = [];
  let last = 0;
  for (const m of src.matchAll(INLINE_TOKEN_PATTERN)) {
    const tok = FORM_TOKENS.find(t => t.id === m[1]);
    if (!tok) continue;
    const start = m.index ?? 0;
    if (start > last) segments.push({ type: 'text', value: src.slice(last, start) });
    segments.push({ type: 'token', value: tok.label });
    last = start + m[0].length;
  }
  if (last < src.length) segments.push({ type: 'text', value: src.slice(last) });
  return segments;
};

// ── Field border / background CSS ────────────────────────────────────────────

/** Resolve a border color key to its hex (defaults to the first palette entry). */
export const borderHex = (color?: FormBorderColor): string =>
  FORM_BORDER_COLORS.find(c => c.value === color)?.hex ?? FORM_BORDER_COLORS[0].hex;

/** Plain CSS style object for a field border — spreadable into any `style` prop.
 * Returns {} for none, so callers can fall back to their placeholder styling. */
export const fieldBorderCss = (border?: FormFieldBorder): Record<string, string> => {
  if (!border || border.style === 'none') return {};
  const hex = borderHex(border.color);
  const w = `${border.width && border.width > 0 ? border.width : 1}px`;
  // `border: none` first so this fully replaces any default border class on the
  // element (box styles override it; underline keeps only the bottom edge).
  if (border.style === 'underline') return { border: 'none', borderBottom: `${w} solid ${hex}` };
  return { border: `${w} ${border.style} ${hex}` };
};

/** Resolve a fill color key to its hex, or null when there's no fill (transparent). */
export const fillHex = (color?: FormFillColor): string | null =>
  color && color !== 'none' ? (FORM_FILL_COLORS.find(c => c.value === color)?.hex ?? null) : null;

/** Plain CSS style object for a field background — {} when none (transparent). Pairs
 * the fill with a legible text `color` so the value stays readable: without it the
 * value text keeps the page's inherited dark color and disappears against a dark fill
 * (most visibly a dropdown's selected value on mobile, where the native <select>
 * paints its value in that color). Every fill in the palette is dark except white, so
 * the text is white on any fill and near-black only on white — revisit if a light
 * fill color is ever added to FORM_FILL_COLORS. */
export const fieldBackgroundCss = (color?: FormFillColor): Record<string, string> => {
  const hex = fillHex(color);
  if (!hex) return {};
  return { backgroundColor: hex, color: color === 'white' ? '#111827' : '#ffffff' };
};

// ── Date field helpers (native <input type="date">) ─────────────────────────
// The stored value is always `MM/DD/YYYY` (matches the PDF render, the date
// tokens, and the "today" auto-fill). A native date input speaks ISO
// `YYYY-MM-DD`, so the fill UIs convert with these. Invalid/empty in → empty
// out, so a blank or legacy value just shows an empty picker (never crashes).
export const mdyToIso = (mdy?: string): string => {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((mdy || '').trim());
  return m ? `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}` : '';
};

export const isoToMdy = (iso?: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((iso || '').trim());
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
};

// ── Conditional visibility ───────────────────────────────────────────────────

/**
 * Whether an element should be shown/rendered given the current field values.
 * No condition → always visible. Checkbox 'false' counts as unfilled. Used by the
 * fill UIs, the required-field check, and the PDF renderer so behavior can't drift.
 */
export const isElementVisible = (
  el: { visibleWhen?: FormCondition },
  values: Record<string, string>
): boolean => {
  const c = el.visibleWhen;
  if (!c || !c.fieldId) return true;
  const v = values[c.fieldId] ?? '';
  switch (c.operator) {
    case 'equals':
      return v === (c.value ?? '');
    case 'notEquals':
      return v !== (c.value ?? '');
    case 'isFilled':
      return v !== '' && v !== 'false';
    case 'isEmpty':
      return v === '' || v === 'false';
    default:
      return true;
  }
};

/**
 * Element ids that are visible once `visibleWhen` is resolved TRANSITIVELY.
 *
 * `isElementVisible` only checks the controller's raw value — it does NOT consider
 * whether the controller is itself hidden. So if A (visibleWhen B=yes) hides while
 * keeping its value `foo`, a naive check would still show C (visibleWhen A=foo).
 * A dependent whose controller is itself hidden is hidden too — regardless of
 * operator (otherwise notEquals/isEmpty would treat the absent value as a match and
 * leak the field onto the document). Memoized DFS with a cycle guard (a malformed
 * self/loop reference — which the builder shouldn't produce — resolves to visible
 * rather than hanging).
 */
export const resolveVisibleElementIds = (
  elements: { id: string; visibleWhen?: FormCondition }[],
  values: Record<string, string>
): Set<string> => {
  const byId = new Map(elements.map(e => [e.id, e]));
  const memo = new Map<string, boolean>();
  const visiting = new Set<string>();

  const isVisible = (id: string): boolean => {
    const cached = memo.get(id);
    if (cached !== undefined) return cached;
    const el = byId.get(id);
    if (!el) return false; // references an element that no longer exists
    const c = el.visibleWhen;
    if (!c || !c.fieldId) {
      memo.set(id, true);
      return true;
    }
    if (visiting.has(id)) return true; // cycle: don't lock the chain out
    visiting.add(id);
    const controllerVisible = isVisible(c.fieldId);
    // A field gated behind a hidden controller is itself hidden — regardless of
    // operator. Re-evaluating against an empty value would wrongly SHOW it for
    // notEquals/isEmpty ('' !== 'foo', '' === ''), leaking it onto the document.
    const result =
      controllerVisible && isElementVisible(el, { [c.fieldId]: values[c.fieldId] ?? '' });
    visiting.delete(id);
    memo.set(id, result);
    return result;
  };

  const out = new Set<string>();
  for (const el of elements) {
    if (isVisible(el.id)) out.add(el.id);
  }
  return out;
};

/**
 * Drop values belonging to conditionally-hidden elements (resolved transitively),
 * so a field typed-then-hidden is neither rendered onto the legal document nor
 * persisted to the patient record. Keys that aren't a known element id are left
 * untouched. Apply this once, server-side, before render AND persist — it's the
 * authoritative gate; the fill UIs' effect-based clearing is only best-effort.
 */
export const retainVisibleValues = (
  elements: { id: string; visibleWhen?: FormCondition }[],
  values: Record<string, string>
): Record<string, string> => {
  const visibleIds = resolveVisibleElementIds(elements, values);
  const elementIds = new Set(elements.map(e => e.id));
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(values)) {
    if (elementIds.has(k) && !visibleIds.has(k)) continue; // known + hidden → drop
    out[k] = v;
  }
  return out;
};

// ── Hide if token missing ────────────────────────────────────────────────────
// Per-element rule (Auto-Populate Token elements and Text Blocks with ≥1 token):
// when on, the element is dropped from the generated form if its token value(s)
// don't exist for the patient. Only tokens from HIDE_IF_TOKEN_MISSING_SOURCES
// (patient demographics + the three insurance ranks) count.

const TOKEN_SOURCE_BY_ID = new Map<string, FormTokenSource>(FORM_TOKENS.map(t => [t.id, t.source]));

/** Token ids referenced by an element: a token element's own token, or the inline
 * tokens of a static-text block. Other element types reference none. */
export const tokenIdsForElement = (el: CustomFormElement): string[] => {
  if (el.type === 'token') return el.tokenId ? [el.tokenId] : [];
  if (el.type === 'staticText') return extractInlineTokenIds(el.text);
  return [];
};

/** Of an element's tokens, those eligible for the hide-if-missing rule. */
const hideEligibleTokenIds = (el: CustomFormElement): string[] =>
  tokenIdsForElement(el).filter(id => {
    const source = TOKEN_SOURCE_BY_ID.get(id);
    return !!source && HIDE_IF_TOKEN_MISSING_SOURCES.includes(source);
  });

/** Whether the builder should offer the "Hide if Token Missing" toggle for this
 * element (token element or text block referencing ≥1 eligible-source token). */
export const supportsHideIfTokenMissing = (el: CustomFormElement): boolean =>
  (el.type === 'token' || el.type === 'staticText') && hideEligibleTokenIds(el).length > 0;

/**
 * Whether an element should be dropped from the generated form because its
 * hide-if-missing token(s) resolved empty. `tokenValues` is keyed by token id
 * (the resolver's output). A block is hidden if ANY of its eligible tokens is
 * missing — so a sentence built around a token never renders with a blank gap.
 */
export const isElementHiddenByMissingToken = (
  el: CustomFormElement,
  tokenValues: Record<string, string>
): boolean => {
  if (!el.hideIfTokenMissing) return false;
  const ids = hideEligibleTokenIds(el);
  if (ids.length === 0) return false;
  return ids.some(id => !(tokenValues[id] ?? '').trim());
};

// ── Default values & required-field checks ───────────────────────────────────

/**
 * The author-set default value for a fillable element, keyed the same way the
 * fill UIs store it: an option id for radio/dropdown, `'true'` for a pre-checked
 * box, raw text/number otherwise. Returns undefined when no default is set, so
 * seeding callers can skip it. Tokens and auto-today dates are seeded separately.
 */
export const elementDefaultValue = (el: CustomFormElement): string | undefined => {
  switch (el.type) {
    case 'singleLine':
    case 'multiLine':
    case 'number':
      return el.value || undefined;
    case 'radio':
    case 'dropdown':
      return el.selectedOptionId || undefined;
    case 'checkbox':
      return el.checked ? 'true' : undefined;
    default:
      return undefined;
  }
};

/**
 * Element ids of required, currently-visible fields that have no value yet.
 * Signature values are stored as data URLs in the same map, so this works for the
 * staff review screen and the builder preview (both key signatures by element id).
 * The patient portal tracks signatures in a separate map and has its own check.
 */
export const unfilledRequiredFieldIds = (
  elements: CustomFormElement[],
  values: Record<string, string>
): string[] =>
  elements
    .filter(el => {
      if (!el.required || !isElementVisible(el, values)) return false;
      switch (el.type) {
        case 'checkbox':
          return values[el.id] !== 'true';
        case 'signature':
        case 'singleLine':
        case 'multiLine':
        case 'date':
        case 'radio':
        case 'dropdown':
        case 'number':
          return !values[el.id];
        default:
          return false;
      }
    })
    .map(el => el.id);
